import Vue from "vue";
import { ValidationProvider, extend, configure } from "vee-validate";
import {
  required,
  email,
  min,
  max,
  confirmed,
  alpha_spaces,
  alpha_num,
  regex,
  min_value,
  max_value,
  between,
  digits,
  alpha_dash,
  integer,
} from "vee-validate/dist/rules";
import { i18n } from "@/i18n";

export const normalizeUrlValue = (url?: string | null): string => {
  return (url || "").trim();
};

export const isUrlMinimalFormatValid = (
  value?: string | null,
  options: { allowEmpty?: boolean } = {}
): boolean => {
  const normalizedUrl = normalizeUrlValue(value);
  if (!normalizedUrl.length) {
    return !!options.allowEmpty;
  }

  try {
    const parsedUrl = new URL(normalizedUrl);
    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

configure({
  defaultMessage: (field, values): string =>
    i18n.t(`validation.messages.${values?._rule_}`, values) as string,
});

extend("required", required);
extend("email", email);
extend("min", min);
extend("max", max);
extend("min_value", min_value);
extend("max_value", max_value);
extend("between", between);
extend("confirmed", confirmed);
extend("alpha_num", alpha_num);
extend("alpha_dash", alpha_dash);
extend("alpha_spaces", alpha_spaces);
extend("regex", regex);
extend("digits", digits);
extend("integer", integer);

extend("phone", {
  validate: (value) => {
    const patt = new RegExp(/^[+]?\(?\d{1,3}\)?[-\s./0-9]*$/g);
    return patt.test(value);
  },
  message: () => i18n.t("errorValidator.phone") as string,
});

extend("url-minimal", {
  validate: (value) => isUrlMinimalFormatValid(value, { allowEmpty: true }),
  message: () => i18n.t("validation.messages.url") as string,
});

extend("min-interval-hours", {
  validate: (value, args) => {
    const stringArgs = args as string[];
    const minimunIntervalTime = stringArgs[0] ? Number(stringArgs[0]) : undefined;
    const initalTime = stringArgs[1];
    const finalTime = stringArgs[2];
    if (
      !minimunIntervalTime ||
      !initalTime ||
      !finalTime ||
      initalTime?.split(":").length < 2 ||
      finalTime?.split(":").length < 2
    ) {
      return true;
    }

    const initialHour = initalTime.split(":")[0];
    const initialMinute = initalTime.split(":")[1];
    const finalHour = finalTime.split(":")[0];
    const finalMinute = finalTime.split(":")[1];
    const difHour = Number(finalHour) - Number(initialHour);
    const difMinute = Number(finalMinute) - Number(initialMinute);

    return !(difHour < minimunIntervalTime || (difHour === minimunIntervalTime && difMinute < 0));
  },
  message: () => i18n.t("validation.messages.minimumRangeIntervalHour") as string,
});
extend("min-starting-interval", {
  validate: (value, args) => {
    const stringArgs = args as string[];

    if (stringArgs.length < 2 || !stringArgs[0] || !stringArgs[1] || !value) {
      return true;
    }

    const minDiff = stringArgs[0];
    const date = new Date(stringArgs[1]);
    date.setHours(value.split(":")[0]);
    date.setMinutes(value.split(":")[1]);
    date.setSeconds(value.split(":")[2]);
    const diff = (date.getTime() - new Date().getTime()) / 36e5;

    return diff >= Number(minDiff);
  },
  message: () => i18n.t("validation.messages.minimumStartingInterval") as string,
});
extend("min-hour", {
  validate: (value, args) => {
    const stringArgs = args as string[];

    if (stringArgs.length < 1 || !stringArgs[0] || !value) {
      return true;
    }

    const minHour = stringArgs[0];
    const hour = value.split(":")[0];

    return Number(hour) >= Number(minHour);
  },
  message: () => i18n.t("validation.messages.initialHour") as string,
});
extend("max-hour", {
  validate: (value, args) => {
    const stringArgs = args as string[];

    if (stringArgs.length < 1 || !stringArgs[0] || !value) {
      return true;
    }

    const maxHour = stringArgs[0];
    const [maxHourH] = maxHour.split(":").map(Number);
    const [valueH, valueM, valueS] = value.split(":").map(Number);

    if (valueH < maxHourH) {
      return true;
    }
    if (valueH === maxHourH && valueM === 0 && valueS === 0) {
      return true;
    }
    return false;
  },
  message: () => i18n.t("validation.messages.finalHour") as string,
});

extend("gt", {
  validate: (value, args) => {
    const stringArgs = args as string[];
    if (stringArgs.length < 1 || !stringArgs[0] || !value) {
      return true;
    }
    const minValue = Number(stringArgs[0]);

    return Number(value) > minValue;
  },
  message: (key, args) => i18n.t("validation.messages.greaterThan", args) as string,
});

extend("invalid-date-pickup", {
  validate: (value, args) => {
    const stringArgs = args as string[];
    const selectedDate = new Date(value);
    selectedDate.setHours(0, 0, 0, 0);

    const now = new Date();
    const nowString = now.toDateString();

    const startDate = new Date(nowString + " " + stringArgs[0]);
    const endDate = new Date(nowString + " " + stringArgs[1]);

    if (stringArgs[0] && stringArgs[1] && now >= startDate && now <= endDate) {
      const newDate = new Date(now);
      newDate.setDate(now.getDate() + 2);
      newDate.setHours(0, 0, 0, 0);
      return selectedDate.getTime() >= newDate.getTime();
    } else {
      return true;
    }
  },
  message: () => i18n.t("validation.messages.date_invalid") as string,
});

extend("maxTotal-commodity", {
  validate: (value, args) => {
    const [maxTotal] = args as string[];

    if (!maxTotal || value === null || value === undefined || value === "") {
      return true;
    }
    return Number(value) <= Number(maxTotal);
  },
  message: () => i18n.t("message.material.cantidadMaxima") as string,
});

extend("file-is-permitted", {
  validate: (value, args) => {
    const allowedTypes = args as string[];
    if (!value || !allowedTypes || allowedTypes.length === 0) {
      return true;
    }
    const fileName = typeof value === "string" ? value : "";
    if (!fileName) {
      return true;
    }
    const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";
    return allowedTypes.map((t) => t.toLowerCase()).includes(fileExtension);
  },
  message: () => i18n.t("label.validation.file.typenotallowed") as string,
});

Vue.component("ValidationProvider", ValidationProvider);
